import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../lib/usePageMeta';
import { getArticles, getStrapiMediaUrl, formatDate } from '../lib/strapi';

export default function BlogHub() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  usePageMeta({
    title: 'Blog - Wricoo',
    description: 'Insights, tips, and strategies for content teams, SEO professionals, and digital marketers. Learn how to streamline your content operations.',
    robots: 'index, follow',
    canonical: 'https://wricoo.com/blog/',
  });

useEffect(() => {
    async function fetchArticles() {
        setLoading(true);
        try {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 10000); // 10s timeout
            const data = await getArticles();
            clearTimeout(timeout);
            setArticles(data);
        } catch (err) {
            console.error('Failed to load articles:', err);
            setArticles([]);
        } finally {
            setLoading(false);
        }
    }
    fetchArticles();
}, []);

  return (
    <div className="min-h-screen bg-white" data-testid="blog-hub-page">

      {/* Header */}
      <header className="border-b border-blue-200 bg-blue-600 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-blue-200 hover:text-white transition"><ArrowLeft className="w-5 h-5" /></Link>
            <Link to="/" className="text-xl font-bold text-white tracking-tight">Wricoo</Link>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/about/" className="text-sm font-medium text-blue-100 hover:text-white transition">About</Link>
            <Link to="/pricing/" className="text-sm font-medium text-blue-100 hover:text-white transition">Pricing</Link>
            <Link to="/tools/" className="text-sm font-medium text-blue-100 hover:text-white transition">Tools</Link>
            <Link to="/blog/" className="text-sm font-medium text-white transition">Blog</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white hover:bg-blue-500 hover:text-white" onClick={() => navigate('/login/')}>Sign In</Button>
            <Button size="sm" className="bg-white text-blue-600 hover:bg-blue-50" onClick={() => navigate('/register/')}>Get Started</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="font-bold text-gray-900 leading-tight mb-4">Wricoo Blog</h1>
          <p className="text-gray-600 leading-relaxed">Insights, tips, and strategies for content teams and SEO professionals.</p>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {loading ? (
            <div className="flex items-center justify-center py-20" data-testid="blog-loading">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600">Loading articles...</span>
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20" data-testid="blog-empty">
              <p className="text-gray-500 mb-4">No articles published yet.</p>
              <p className="text-gray-400 text-sm">Check back soon for new content!</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
              {articles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-bold text-gray-900 mb-4">Ready to Streamline Your Content Operations?</h2>
          <p className="text-gray-600 mb-6">Join content teams who trust Wricoo to manage their SEO briefs and content workflows.</p>
          <Button size="lg" onClick={() => navigate('/register/')}>
            Start for Free <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} Wricoo. All rights reserved.</p>
          <nav className="flex gap-4 text-sm text-gray-500">
            <a href="/about/" className="hover:text-gray-900 transition">About</a>
            <a href="/pricing/" className="hover:text-gray-900 transition">Pricing</a>
            <a href="/tools/" className="hover:text-gray-900 transition">Tools</a>
            <a href="/blog/" className="hover:text-gray-900 transition">Blog</a>
            <a href="/privacy-policy/" className="hover:text-gray-900 transition">Privacy Policy</a>
            <a href="/terms-and-conditions/" className="hover:text-gray-900 transition">Terms</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function ArticleCard({ article }) {
  const coverImage = article.cover ? getStrapiMediaUrl(article.cover) : null;
  const category = article.category;
  const author = article.author;
  
  return (
    <article className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow" data-testid={`article-card-${article.id}`}>
      {/* Feature Image */}
      <a href={`/blog/${article.slug}/`} className="block">
        {coverImage ? (
          <img 
            src={coverImage} 
            alt={article.title || ''} 
            className="w-full h-48 object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-48 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
            <span className="text-blue-400 text-4xl font-bold">W</span>
          </div>
        )}
      </a>
      
      {/* Content */}
      <div className="p-5">
        {/* Category Badge */}
        {category && (
          <span className="inline-block text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded mb-3">
            {category.name}
          </span>
        )}
        
        {/* Title */}
        <h2 className="font-bold text-gray-900 mb-2 line-clamp-2 hover:text-blue-600 transition">
          <a href={`/blog/${article.slug}/`}>{article.title}</a>
        </h2>
        
        {/* Description */}
        {article.description && (
          <p className="text-gray-600 text-sm mb-4 line-clamp-2">{article.description}</p>
        )}
        
        {/* Meta */}
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(article.publishedAt)}</span>
          </div>
          <a href={`/blog/${article.slug}/`} className="text-blue-600 font-medium hover:underline flex items-center gap-1">
            Read More <ArrowRight className="w-3 h-3" />
          </a>
        </div>
      </div>
    </article>
  );
}
